// 브라우저 기본 <select> 대신 쓰는 드롭다운 (2026-10-19 김현비 "드롭다운 이쁘게")
// 기본 <select> 는 펼친 목록을 브라우저가 파란 사각형으로 그려서 사이트 톤과 따로 논다.
// 쓰는 법은 <select> 와 같다: <Select value onChange className style><option value>..</option></Select>
// onChange 에는 { target: { value } } 를 넘긴다(값은 기본 select 처럼 문자열).
// 목록은 body 에 띄워서(portal) 표·모달의 overflow 에 잘리지 않는다.
import { Children, isValidElement, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./Select.css";

function collectOptions(children, out = []) {
  Children.forEach(children, (child) => {
    if (!isValidElement(child)) return;
    if (child.type === "option") {
      const label = child.props.children;
      const value = child.props.value !== undefined ? child.props.value : label;
      out.push({ value: String(value), label, disabled: !!child.props.disabled });
    } else if (child.props && child.props.children) {
      collectOptions(child.props.children, out); // optgroup·Fragment
    }
  });
  return out;
}

export default function Select({ value, onChange, className = "", style, disabled, children, ...rest }) {
  const options = collectOptions(children);
  const cur = String(value ?? "");
  const selected = options.find((o) => o.value === cur) || options[0];
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const [hover, setHover] = useState(-1);
  const btnRef = useRef(null);
  const listRef = useRef(null);

  const place = () => {
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    const listH = Math.min(options.length * 38 + 12, 300);
    const below = window.innerHeight - r.bottom;
    const up = below < listH + 8 && r.top > below;
    setPos({ left: r.left, width: Math.max(r.width, 140), top: up ? r.top - listH - 6 : r.bottom + 6 });
  };

  useLayoutEffect(() => {
    if (open) place();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const close = (e) => {
      if (btnRef.current?.contains(e.target) || listRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") { setOpen(false); btnRef.current?.focus(); }
    };
    const onScroll = (e) => {
      if (listRef.current?.contains(e.target)) return;
      place();
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", place);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", place);
    };
  }, [open]);

  const pick = (o) => {
    if (o.disabled) return;
    setOpen(false);
    if (o.value !== cur) onChange?.({ target: { value: o.value } });
  };

  const onBtnKey = (e) => {
    if (disabled) return;
    if (!open && (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ")) {
      e.preventDefault();
      setOpen(true);
      setHover(Math.max(0, options.findIndex((o) => o.value === cur)));
      return;
    }
    if (!open) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHover((h) => Math.min(options.length - 1, h + 1)); }
    if (e.key === "ArrowUp") { e.preventDefault(); setHover((h) => Math.max(0, h - 1)); }
    if (e.key === "Enter" && options[hover]) { e.preventDefault(); pick(options[hover]); }
  };

  return (
    <>
      <button
        type="button"
        ref={btnRef}
        className={`cs-select ${className} ${open ? "open" : ""}`}
        style={style}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => !disabled && setOpen((v) => !v)}
        onKeyDown={onBtnKey}
        {...rest}
      >
        <span className="cs-select-text">{selected ? selected.label : ""}</span>
        <svg className="cs-select-caret" width="12" height="12" viewBox="0 0 12 12" aria-hidden>
          <path d="M3 4.5 6 7.5 9 4.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && pos && createPortal(
        <ul
          ref={listRef}
          className="cs-select-list"
          role="listbox"
          style={{ left: pos.left, top: pos.top, minWidth: pos.width }}
        >
          {options.map((o, i) => (
            <li
              key={o.value + i}
              role="option"
              aria-selected={o.value === cur}
              className={`${o.value === cur ? "on" : ""} ${i === hover ? "hover" : ""} ${o.disabled ? "disabled" : ""}`}
              onMouseEnter={() => setHover(i)}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(o)}
            >
              <span>{o.label}</span>
              {o.value === cur && <i aria-hidden>✓</i>}
            </li>
          ))}
        </ul>,
        document.body
      )}
    </>
  );
}
